import { inject, provide, ref } from 'vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useRouter } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
import store from 'dashboard/store';

const SidebarControl = Symbol('SidebarControl');

const DEFAULT_WIDTH = 200;
const MIN_WIDTH = DEFAULT_WIDTH;
const MAX_WIDTH = 320;

export function useSidebarResize() {
  const { uiSettings, updateUISettings } = useUISettings();

  const initialWidth = uiSettings.value.sidebar_width ?? DEFAULT_WIDTH;
  const sidebarWidth = ref(
    Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, initialWidth))
  );

  const setSidebarWidth = width => {
    sidebarWidth.value = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
  };

  const saveWidth = () => {
    updateUISettings({ sidebar_width: sidebarWidth.value });
  };

  return {
    sidebarWidth,
    setSidebarWidth,
    saveWidth,
    MIN_WIDTH,
    MAX_WIDTH,
    DEFAULT_WIDTH,
  };
}

export function useSidebarContext() {
  const context = inject(SidebarControl, null);
  if (context === null) {
    throw new Error(`Component is missing a parent <Sidebar /> component.`);
  }

  const router = useRouter();
  const { shouldShow } = usePolicy();

  const resolvePath = to => {
    if (!to) return '/';
    try {
      return router.resolve(to)?.path || '/';
    } catch {
      return '/';
    }
  };

  // Helper to find route definition by name without resolving
  const findRouteByName = name => {
    const routes = router.getRoutes();
    return routes.find(route => route.name === name);
  };

  const resolvePermissions = to => {
    if (!to) return [];
    try {
      if (to.params?.navigationPath) {
        const targetRoute = findRouteByName(to.params.navigationPath);
        return targetRoute?.meta?.permissions ?? [];
      }
      return router.resolve(to)?.meta?.permissions ?? [];
    } catch {
      return [];
    }
  };

  const resolveFeatureFlag = to => {
    if (!to) return '';
    try {
      if (to.params?.navigationPath) {
        const targetRoute = findRouteByName(to.params.navigationPath);
        return targetRoute?.meta?.featureFlag || '';
      }
      return router.resolve(to)?.meta?.featureFlag || '';
    } catch {
      return '';
    }
  };

  const resolveInstallationType = to => {
    if (!to) return [];
    try {
      if (to.params?.navigationPath) {
        const targetRoute = findRouteByName(to.params.navigationPath);
        return targetRoute?.meta?.installationTypes || [];
      }
      return router.resolve(to)?.meta?.installationTypes || [];
    } catch {
      return [];
    }
  };

  const isAllowed = to => {
    const user = store.getters.getCurrentUser;
    if (user?.type === 'SuperAdmin') return true;

    const accountId = store.getters.getCurrentAccountId;
    const account = user?.accounts?.find(
      a => Number(a.id) === Number(accountId)
    );
    if (account?.role === 'administrator') return true;

    const permissions = resolvePermissions(to);
    const featureFlag = resolveFeatureFlag(to);
    const installationType = resolveInstallationType(to);

    return shouldShow(featureFlag, permissions, installationType);
  };

  return {
    ...context,
    resolvePath,
    resolvePermissions,
    resolveFeatureFlag,
    isAllowed,
  };
}

export function provideSidebarContext(context) {
  provide(SidebarControl, context);
}
